import { useEffect, useState } from 'react';
import { supabase } from '~/services/supabase';
import type { IFormSchema } from '~/types';

interface UseFormSchemaOptions {
  enabled?: boolean;
}

interface UseFormSchemaResult {
  schema: IFormSchema | null;
  formId: string | null;
  formVersion: number | null;
  loading: boolean;
  error: string | null;
}

export const useFormSchema = ({ enabled = true }: UseFormSchemaOptions = {}): UseFormSchemaResult => {
  const [schema, setSchema] = useState<IFormSchema | null>(null);
  const [formId, setFormId] = useState<string | null>(null);
  const [formVersion, setFormVersion] = useState<number | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const fetchFormSchema = async () => {
      try {
        // Get form ID from URL parameter
        const params = new URLSearchParams(window.location.search);
        const id = params.get('formId');

        if (!id) {
          throw new Error('No form ID provided in URL');
        }

        setFormId(id);

        // Fetch published form from Supabase
        const { data, error: fetchError } = await supabase
          .from('forms')
          .select('id, json_schema, version')
          .eq('id', id)
          .eq('status', 'published')
          .single();

        if (fetchError) {
          throw new Error(`Failed to fetch form: ${fetchError.message}`);
        }

        if (!data) {
          throw new Error('Form not found or not published');
        }

        setFormVersion(data.version);
        setSchema(data.json_schema as IFormSchema);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        console.error('Error fetching form schema:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFormSchema();
  }, [enabled]);

  return { schema, formId, formVersion, loading, error };
};
